import { DataItem } from "@/types";

function flattenObject<T extends Record<string, unknown>>(
  obj: T,
  prefix = ""
): Record<string, string> {
  const flattened: Record<string, string> = {};

  for (const key in obj) {
    if (obj[key] === null || obj[key] === undefined) {
      flattened[prefix + key] = "";
      continue;
    }

    if (Array.isArray(obj[key])) {
      // For arrays, join the values with a semicolon
      flattened[prefix + key] = obj[key]
        .map(
          (
            item: Record<string, unknown> | string | number | boolean | null
          ) => {
            if (item && typeof item === "object") {
              // If array contains objects, get their values
              return Object.values(item).join(", ");
            }
            return String(item ?? "");
          }
        )
        .join("; ");
    } else if (typeof obj[key] === "object" && !(obj[key] instanceof Date)) {
      // Recursively flatten nested objects
      const nested = flattenObject(
        obj[key] as Record<string, unknown>,
        `${prefix}${key}_`
      );
      Object.assign(flattened, nested);
    } else if (obj[key] instanceof Date) {
      // Format dates
      flattened[prefix + key] = obj[key].toISOString();
    } else {
      // Handle phone numbers that should have a plus sign
      const value = obj[key].toString();
      if (
        (key === "phone" || key === "cell" || key === "mobile") &&
        value.match(/^61/)
      ) {
        flattened[prefix + key] = "+" + value;
      } else {
        flattened[prefix + key] = value;
      }
    }
  }

  return flattened;
}

export function exportToCSV<T extends DataItem>(
  data: T[],
  filename: string,
  fields?: (keyof T)[]
): void {
  if (!data.length) return;

  // Flatten the first object to get all possible headers
  const flattenedFirst = flattenObject(data[0]);
  let headers = Object.keys(flattenedFirst);

  // If fields are specified, filter headers to only include those fields
  if (fields) {
    headers = headers.filter((header) =>
      fields.some(
        (field) => header === field || header.startsWith(`${field.toString()}_`)
      )
    );
  }

  // Create CSV content
  const csvContent = [
    // Headers row
    headers.join(","),
    // Data rows
    ...data.map((item) => {
      const flattenedItem = flattenObject(item);
      return headers
        .map((header) => {
          const value = flattenedItem[header] || "";
          // Escape values containing commas or quotes
          return value.includes(",") || value.includes('"')
            ? `"${value.replace(/"/g, '""')}"`
            : value;
        })
        .join(",");
    }),
  ].join("\n");

  // Create and trigger download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", `${filename}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
